import React, { useMemo, useState } from "react";
import { Button, Form, Input, Label } from "reactstrap";
import ToastAlert from "../../Components/Common/ToastAlert";
import { APIClient } from "../../helpers/api_helper";

const AddKeywordModal = ({ isOpen, toggle, existingLocations, getInputList }) => {
    const api = useMemo(() => new APIClient(), []);

    const [keyword, setKeyword] = useState("");
    const [selectedLocations, setSelectedLocations] = useState([]);

    const allSelected = existingLocations.length > 0 && selectedLocations.length === existingLocations.length;

    const toggleAll = () => {
        if (allSelected) {
            setSelectedLocations([]);
        } else {
            setSelectedLocations(existingLocations.map(l => `${l.location}|${l.country}`));
        }
    };

    const toggleLocation = (key) => {
        setSelectedLocations(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleModalClick = (e) => {
        if (e.target.classList.contains("modal")) {
            toggle();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!keyword.trim()) {
            ToastAlert("Keyword cannot be empty.", "warning");
            return;
        }
        if (selectedLocations.length === 0) {
            ToastAlert("Please select at least one location.", "warning");
            return;
        }
        try {
            const locs = existingLocations.filter(l => selectedLocations.includes(`${l.location}|${l.country}`));
            await Promise.all(
                locs.map(loc =>
                    api.update("v1/inputs/", { location: loc.location, keyword: keyword.trim(), country: loc.country })
                )
            );
            await getInputList();
            toggle();
            ToastAlert(`${locs.length} combination(s) added successfully.`, "success");
        } catch (error) {
            ToastAlert(error, "error");
        }
    };

    return (
        <React.Fragment>
            <div
                className={`modal fade ${isOpen ? "show" : ""}`}
                tabIndex="-1"
                role="dialog"
                style={{ display: isOpen ? "block" : "none" }}
                onClick={handleModalClick}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header border-bottom-0">
                            <h5 className="modal-title">Add Keyword</h5>
                            <button type="button" className="btn-close" onClick={toggle}></button>
                        </div>
                        <div className="modal-body">
                            <Form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <Label className="form-label">
                                        Keyword <span className="text-danger">(*)</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        autoFocus
                                        className="form-control"
                                        value={keyword}
                                        onChange={e => setKeyword(e.target.value)}
                                        placeholder="e.g. JavaScript"
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Label className="form-label mb-0">
                                            Match with Locations <span className="text-danger">(*)</span>
                                        </Label>
                                        {existingLocations.length > 0 && (
                                            <Button type="button" size="sm" color="secondary" outline onClick={toggleAll}>
                                                {allSelected ? "Deselect All" : "Select All"}
                                            </Button>
                                        )}
                                    </div>
                                    {existingLocations.length === 0 ? (
                                        <p className="text-muted small">No locations yet.</p>
                                    ) : (
                                        <div className="border rounded p-2" style={{ maxHeight: "220px", overflowY: "auto" }}>
                                            {existingLocations.map(l => {
                                                const key = `${l.location}|${l.country}`;
                                                return (
                                                    <div key={key} className="form-check mb-1">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`kw-loc-${key}`}
                                                            checked={selectedLocations.includes(key)}
                                                            onChange={() => toggleLocation(key)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`kw-loc-${key}`}>
                                                            {l.location} ({l.country})
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="hstack gap-2 justify-content-end">
                                    <Button type="button" className="btn btn-light" onClick={toggle}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="btn btn-primary">
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>
            {isOpen && <div className="modal-backdrop fade show"></div>}
        </React.Fragment>
    );
};

export default AddKeywordModal;
